@use 'sass:math';
@use '../../scss/utility';
@use './value-slider-vars';

$circle-size: utility.wb-pixel-to-rem(24px);
$update-transition-duration: 0.05s;

:host {
  @include value-slider-vars.wb-value-slider-vars;
  display: block;
  width: 100%;
}

.inner {
  width: 100%;
  position: relative;
  height: $circle-size;
}

.label {
  color: var(--value-slider-text-color);
  display: block;
  padding-inline-start: math.div($circle-size, 2);
  margin-block-end: utility.wb-pixel-to-rem(4px);
  cursor: pointer;
}

input {
  appearance: none;
  height: $circle-size;
  background: transparent;
  border: 0;
  outline: 0;
  margin: 0;
  padding: 0;
  cursor: pointer;
  width: 100%;
  opacity: 0;

  &::-webkit-slider-thumb {
    appearance: none;
    width: $circle-size;
    height: $circle-size;
  }

  &::-moz-range-thumb {
    appearance: none;
    width: $circle-size;
    height: $circle-size;
  }
}

.dots {
  display: flex;
  justify-content: space-between;
}

.dot {
  background-color: var(--value-slider-scale-indicator-color);
  width: utility.wb-pixel-to-rem(4px);
  height: utility.wb-pixel-to-rem(4px);
  border-radius: var(--wb-radius-s);
  z-index: 1;
}

input:not(:disabled):hover ~ .overlay {
  @include value-slider-vars.wb-value-slider-hover-vars;
}

input:not(:disabled):focus ~ .overlay .indicator {
  @include value-slider-vars.wb-value-slider-focus-vars;
  border: var(--value-slider-indicator-border-weight) solid var(--value-slider-indicator-border-ring-color);
}

input:not(:disabled):active ~ .overlay .indicator,
input:not(:disabled).active ~ .overlay .indicator {
  @include value-slider-vars.wb-value-slider-active-vars;

  box-shadow: initial;

  // tooltip implemented via before and after pseudo elements

  &::before {
    content: '';
    width: utility.wb-pixel-to-rem(11px);
    height: utility.wb-pixel-to-rem(11px);
    position: absolute;
    transform: translateX(-5px) rotate(45deg);
    background: var(--value-slider-tooltip-background-color);
    inset-block-start: utility.wb-pixel-to-rem(-22px);
    inset-inline-start: 50%;
    border-radius: 0 0 utility.wb-pixel-to-rem(2px) 0;
  }

  &::after {
    @include utility.wb-text(
      's',
      (
        responsive: false,
      )
    );
    background: var(--value-slider-tooltip-background-color);
    border-radius: var(--value-slider-tooltip-border-radius);
    color: var(--value-slider-tooltip-text-color);
    content: attr(data-value);
    display: block;
    inset-inline-start: 50%;
    inset-block-end: calc(100% + #{utility.wb-pixel-to-rem(16px)});
    padding: utility.wb-pixel-to-rem(16px);
    position: absolute;
    transform: translateX(-50%);
    white-space: nowrap;
  }
}

.overlay {
  position: absolute;
  pointer-events: none;
  background: var(--value-slider-bar-empty-color);
  border-radius: var(--wb-radius-s);
  height: utility.wb-pixel-to-rem(4px);
  inset-inline: math.div($circle-size, 2);
  inset-block-start: 50%;
  transform: translateY(-50%);
}

.indicator {
  position: absolute;
  box-sizing: border-box;
  width: $circle-size;
  height: $circle-size;
  background: var(--value-slider-indicator-background-color);
  border-radius: var(--wb-radius-full);
  transform: translate3d(-50%, -10px, 0);
  z-index: 2;
  transition:
    left $update-transition-duration var(--wb-move),
    right $update-transition-duration var(--wb-move);
}

:host([is-rtl]) {
  .indicator {
    transform: translate3d(50%, -10px, 0);
  }
}

.progress-line {
  position: absolute;
  inset-inline-start: 0;
  height: utility.wb-pixel-to-rem(4px);
  background: var(--value-slider-bar-filled-color);
  border-radius: var(--wb-radius-s);
  transition:
    left $update-transition-duration var(--wb-move),
    right $update-transition-duration var(--wb-move);
}

.highlight-area {
  position: absolute;
  inset-inline-start: 0;
  height: utility.wb-pixel-to-rem(4px);
  background: var(--value-slider-bar-range-color);
  border-radius: var(--wb-radius-s);
  mix-blend-mode: multiply;
}

.scale {
  display: flex;
  justify-content: space-between;
  padding: 0 math.div($circle-size, 2);
  height: utility.wb-pixel-to-rem(28px);
  align-items: flex-end;
}

.scale-item {
  position: relative;
  color: var(--value-slider-scale-text-color);

  .wb-text {
    position: absolute;
    inset-block-end: 0;
    inset-inline-start: 0;
    transform: translateX(-50%);
  }

  &:first-child .wb-text {
    transform: translateX(0);
  }

  &:last-child .wb-text {
    transform: translateX(-100%);
  }
}

:host([hide-tooltip]) .indicator {
  &::before,
  &::after {
    display: none !important; // knowingly using !important here to overwrite high specifity selectors
  }
}

:host([disabled]) {
  opacity: var(--value-slider-opacity-disabled);

  input {
    cursor: not-allowed;
  }
}

input,
label {
  -webkit-tap-highlight-color: transparent;
}
